/**
 * EINE TABELLE AUF DEM TELEFON IST EINE LISTE VON KARTEN.
 *
 * ORIGINALTEXT MARVIN (05.09.2026, mit zwei Bildschirmfotos vom iPhone)
 * > „tabelleninhalte werden nict dargstellt, in der praxis macht das
 * >  probleme."
 *
 * **ER HAT RECHT, UND DER BEFUND IST ÄLTER ALS DIESE VERSION.** Auf seinem
 * Bild steht in der Auftragsliste „Kornp…" und darunter „56068" - die
 * Baustelle, also die einzige Spalte, nach der man sucht, ist an der rechten
 * Kante abgeschnitten. Seit dem 24.08.2026 rollt eine zu breite Tabelle
 * seitlich in ihrer Karte, und das war damals die richtige Antwort auf eine
 * Regel, die einfach jede Spalte ab der vierten ausgeblendet hat.
 *
 * **NUR SIEHT MAN EINEM ROLLENDEN BEREICH NICHT AN, DASS ER ROLLT.** Acht
 * Spalten auf 375 Bildpunkten heissen: zweieinhalb sind zu sehen und fünfeinhalb
 * liegen hinter einer Wischgeste, von der nichts erzählt. Das ist derselbe
 * Fehler wie bei der Menüleiste am unteren Rand, nur eine Etage tiefer -
 * **was man wischen muss und was es nicht sagt, ist von etwas Abgeschnittenem
 * nicht zu unterscheiden.**
 *
 * DIE ANTWORT IST NICHT WENIGER INHALT, SONDERN EINE ANDERE FORM. Auf einem
 * schmalen Bildschirm wird aus jeder ZEILE ein Kasten und aus jeder ZELLE
 * eine Zeile mit ihrer Spaltenüberschrift davor. Nichts fällt weg, nichts
 * liegt hinter einer Geste, und die Reihenfolge bleibt dieselbe.
 *
 * **UND DIE BESCHRIFTUNG STEHT AN EINER STELLE.** Es gibt rund hundert
 * `table.liste` in dieser Oberfläche; ein `data-spalte` an jeder Zelle von
 * Hand wäre hundertmal dieselbe Angabe und damit hundert Gelegenheiten, sie
 * zu vergessen - dieselbe Lehre wie beim Seitenrand (fünfundvierzigmal) und
 * bei der Schriftgrösse. Sie wird deshalb aus dem KOPF DER TABELLE gelesen,
 * die ohnehin dasteht.
 *
 * DIESE DATEI TRÄGT KEIN JSX, damit ein Prüfstand sie WIRKLICH ausrechnen
 * kann. Neunte Datei dieser Art nach `namen.ts`, `terminraster.ts`,
 * `klebeband.ts`, `kartenmass.ts`, `oberflaeche.ts`, `kartenebene.ts`,
 * `grenzpunkte.ts` und `handbuchmarken.ts`.
 */

/** Ab so vielen Spalten wird gestapelt. */
export const SPALTEN_AB = 4;

/**
 * DIE ÜBERSCHRIFT EINER SPALTE, OHNE DAS, WAS NICHT DAZUGEHÖRT.
 *
 * Im Kopf mancher Tabellen steht ein Sortierknopf mit einem Pfeil und einer
 * Zahl. **Ein Pfeil ist keine Beschriftung** - er landete sonst vor jeder
 * Zelle der Spalte. Gelesen wird `.sortewort`, wo es eines gibt, sonst der
 * reine Text.
 */
export function spaltenname(th: Element): string {
  const wort = th.querySelector('.sortewort');
  const roh = (wort ?? th).textContent ?? '';
  return roh.replace(/\s+/g, ' ').trim();
}

/**
 * Jeder Zelle ihre Spaltenüberschrift, jeder Tabelle ihre Spaltenzahl.
 *
 * Gibt zurück, wie viele Tabellen beschriftet wurden - der Prüfstand fragt
 * danach: **ein Beschrifter, der nichts findet, ist von einem grünen nicht zu
 * unterscheiden.**
 */
export function beschrifte(wurzel: ParentNode | null | undefined): number {
  if (!wurzel) return 0;
  let getan = 0;
  for (const t of Array.from(wurzel.querySelectorAll('table.liste'))) {
    const kopf = Array.from(t.querySelectorAll('thead th')).map(spaltenname);
    /* OHNE KOPF GIBT ES NICHTS ZU BESCHRIFTEN, und gestapelt wäre die Tabelle
       dann eine Folge von Werten ohne Namen - schlechter als vorher. */
    if (kopf.length < 2) continue;
    t.setAttribute('data-spalten', String(kopf.length));
    /* EIN RASTER STAPELT NICHT (.400): das Wochenraster der NE4-Planung ist
       Team mal Tag, gestapelt wird daraus eine Folge leerer Kästen (Marvin,
       19.10.2026, Bildschirmfoto). Wer `data-stapeln="nein"` setzt, zeichnet
       seine schmale Fassung selbst. */
    if (t.getAttribute('data-stapeln') === 'nein') { getan++; continue; }
    if (kopf.length >= SPALTEN_AB) t.setAttribute('data-stapeln', 'ja');
    else t.removeAttribute('data-stapeln');

    for (const tr of Array.from(t.querySelectorAll('tbody tr'))) {
      const zellen = Array.from(tr.children);
      /* EINE ZEILE, DIE ÜBER ALLE SPALTEN GEHT, IST KEINE DATENZEILE.
         „Nichts gefunden", eine Zwischenüberschrift, ein Ladehinweis: sie
         bekommen keine Beschriftung, sondern die volle Breite. */
      for (let i = 0; i < zellen.length; i++) {
        const z = zellen[i] as HTMLElement;
        const spanne = Number(z.getAttribute('colspan') ?? '1');
        if (spanne > 1 || zellen.length < kopf.length) {
          if (z.getAttribute('data-voll') !== 'ja') z.setAttribute('data-voll', 'ja');
          z.removeAttribute('data-spalte');
          continue;
        }
        z.removeAttribute('data-voll');
        const name = kopf[i] ?? '';
        /* NUR SCHREIBEN, WENN SICH ETWAS ÄNDERT. Jeder Schreibvorgang weckt
           den Beobachter, der diese Funktion gerufen hat; ohne diese Zeile
           ruft er sich selbst, bis der Browser steht. */
        if (z.getAttribute('data-spalte') !== name) z.setAttribute('data-spalte', name);
      }
    }
    getan++;
  }
  return getan;
}

/**
 * Beschriften, und beschriften, sobald sich etwas ändert.
 *
 * Die Tabellen entstehen nach jedem Abruf neu; ein einmaliger Lauf beim
 * Öffnen erwischt genau die erste Fassung. Der Beobachter läuft im
 * NÄCHSTEN Bildschirmtakt, damit hundert Änderungen einer Neuzeichnung EINEN
 * Durchgang ergeben und nicht hundert.
 */
export function beobachteTabellen(wurzel: HTMLElement | null): () => void {
  if (!wurzel || typeof MutationObserver === 'undefined') return () => {};
  let angemeldet = 0;
  const gleich = () => {
    angemeldet = 0;
    beschrifte(wurzel);
  };
  const bald = () => {
    if (angemeldet) return;
    angemeldet = requestAnimationFrame(gleich);
  };
  beschrifte(wurzel);
  const b = new MutationObserver(bald);
  b.observe(wurzel, { childList: true, subtree: true });
  return () => {
    b.disconnect();
    if (angemeldet) cancelAnimationFrame(angemeldet);
  };
}
